import React from 'react';
import { Box, Container, Grid } from '@material-ui/core';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import OrderViewBox from './OrderViewBox';
import { Card, Typography } from '@castiron/components';
import Dinero from 'dinero.js';
import CustomerNotes from './CustomerNotes';
import { LineItem, Transaction } from '@castiron/domain';
import Price from '../Price';
import Tooltip from '../Tooltip';
import { useAppSelector } from '../../hooks';
import { FeatureFlag } from '@castiron/castiron-firebase';

type Props = {
  overrideTitle?: string;
  transaction?: Transaction;
  removePaymentDetails?: boolean;
};

const useStyles = makeStyles((theme: Theme) => ({
  amountsSection: {
    padding: 24,
  },
  customOrderDetailsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'start',
    marginTop: '16px',
    width: '100%',
  },
  customOrderDetails: {
    marginTop: '4px',
    minWidth: '100px',
  },
  customOrderDetailsValues: {
    marginTop: '4px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    flex: 1,
  },
  customDetailsItemContainer: {
    display: 'flex',
    gap: 12,
    padding: '0 8px',
  },
  hasTip: {
    width: '100%',
    padding: 16,
    borderRadius: 8,
    backgroundColor: theme.branding.blue.light,
    [theme.breakpoints.down('sm')]: {
      padding: '12px 12px 8px',
    }
  },
  itemBox: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'row',
    borderBottom: `1px solid ${theme.branding.gray[400]}`,
    alignItems: 'center',
  },
  itemContainer: {
    display: 'flex',
    gap: 12,
    paddingLeft: 10,
  },
  itemDetails: {
    display: 'flex',
    flex: '2',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  itemPrice: {
    alignSelf: 'flex-start',
    fontSize: 14,
    fontWeight: 700,
  },
  notesSection: {
    padding: 24,
    borderTop: `1px solid ${theme.branding.gray[400]}`,
  },
  notesTitle: {
    color: theme.branding.gray[700],
  },
  priceSection: {
    paddingTop: 8,
    paddingBottom: 8,
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  subtitle: {
    color: theme.branding.gray[800],
  },
  totalSection: {
    paddingTop: 12,
  },
  variationItem: {
    color: theme.branding.gray[800],
  },
  variationLabel: {
    color: theme.branding.gray[700],
  },
  innerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  selectionContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'start',
    width: '100%',
    marginTop: 16,
  },
  selection: {
    marginTop: '4px',
    minWidth: '100px',
  },
  selectionValues: {
    marginTop: '4px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    flex: 1,
  },
}));

const DetailsBox: React.FC<Props> = (props: Props) => {
  const { overrideTitle, transaction, removePaymentDetails = false } = props;
  const classes = useStyles();
  const theme = useTheme();

  const capitalize = (val: string) => {
    if (val) return val[0].toUpperCase() + val.slice(1); // to sentence case the fulfillment option type
  };

  const { shop } = useAppSelector(state => ({
    shop: state.shops.shop,
  }));

  const totals = transaction?.totals;
  const tip = totals?.tip || 0;
  const couponTotal = totals?.coupon || 0;
  const couponCode = transaction?.coupon?.code || '';
  const couponDiscount = transaction?.coupon?.discount || null;
  const fulfillmentFee = totals?.fulfillmentFee || 0;
  const fulfillmentType = capitalize(transaction?.order?.fulfillmentOption?.type) || '';
  const stripeFees = totals?.stripeFees || 0;
  const castIronFees = totals?.castIronFees || 0;
  const customerCastironFeePercent = transaction?.tier.castironTakeRate ? totals?.customerFeePercent / transaction?.tier.castironTakeRate : 0;
  const customerCastironFeeAmount = Math.round(castIronFees * customerCastironFeePercent);
  const artisanCastironFeePercent = transaction?.tier.castironTakeRate ? totals?.artisanFeePercent / transaction?.tier.castironTakeRate : 0;
  const artisanCastironFeeAmount = Math.round(castIronFees * artisanCastironFeePercent);
  const customerPaidStripeFees = totals?.customerPaidStripeFees;
  const salesTax = totals?.taxes || 0;
  const total = totals?.total || 0; //subtotal + delivery + fee + tip + salesTax;
  const orderedItems: LineItem[] = transaction?.order?.items
    ? transaction?.order.items
    : transaction?.products.map(p => ({
      id: p.product.id,
      title: p.product.title,
      description: p.product.description,
      category: p.product.category,
      price: p.product.price,
      quantity: p.quantity,
      selections: p.selectedVariationValues,
      type: 'standard',
    }));

  const getPrice = (productPrice, productQuantity) => {
    const curPrice = productPrice * productQuantity;
    return curPrice;
  };

  if (!transaction) return <></>;

  return (
    <>
      <Card title={overrideTitle || "Details"} removeChildPadding>
        {orderedItems.length > 0 &&
          orderedItems.map((item, index) => (
            <>
              {item.type === 'standard' ? (
                <Container key={item.id} className={classes.itemBox}  style={index === orderedItems.length - 1 ? {borderBottom: 'none'} : {}}>
                  <Container className={classes.itemContainer}>
                    <div className={classes.itemDetails}>
                      <Grid container justify="space-between" wrap="nowrap">
                        <Typography variant='body1'>{item.title}</Typography>
                        {item?.price && (
                          <Typography className={classes.itemPrice}>
                            {Dinero({ amount: getPrice(item.price, item.quantity) }).toFormat('$0.00')}
                          </Typography>
                        )}
                      </Grid>
                      <Typography variant='body2'>Qty: <span style={{fontWeight: 700,}}>{item.quantity || 0}</span></Typography>
                      {item.notes ? (
                        <Box className={classes.customOrderDetailsContainer}>
                          <Box className={classes.customOrderDetailsValues}>
                            <Typography variant='body2' className={classes.notesTitle}>Notes</Typography>
                            <Typography variant='body1'>{item.notes}</Typography>
                          </Box>
                        </Box>
                      ) : (
                        <>
                          {transaction.artisanNotes && (
                            <Box className={classes.customOrderDetailsContainer}>
                              <Box className={classes.customOrderDetailsValues}>
                                <Typography variant='body2' className={classes.notesTitle}>Notes</Typography>
                                <Typography>{transaction.artisanNotes}</Typography>
                              </Box>
                            </Box>
                          )}
                        </>
                      )}
                      {!transaction.artisanNotes &&
                        item.selections?.map(s => (
                          <Box key={s.inputFieldId} className={classes.selectionContainer}>
                            <Box className={classes.selection}>
                              <Typography className={classes.variationLabel} variant='body2'>{s.inputFieldName}</Typography>
                            </Box>
                            <Box className={classes.selectionValues}>
                              {s.selectedValues &&
                                s.selectedValues.map(v => (
                                  <Box key={v.id} className={classes.innerContainer}>
                                    <Typography className={classes.variationItem} variant='body1'>{v.name}</Typography>
                                    <Price className={classes.itemPrice} amount={v.cost * item.quantity} />
                                  </Box>
                                ))}
                            </Box>
                          </Box>
                        ))}
                    </div>
                  </Container>
                </Container>
              ) : (
                <>
                  <Container key={item.id} className={classes.itemBox}  style={index === orderedItems.length - 1 ? {borderBottom: 'none'} : {}}>
                    <Container className={classes.itemContainer}>
                      <div className={classes.itemDetails}>
                        <Grid container justify="space-between" wrap="nowrap">
                          <Typography variant='body1'>{item.title}</Typography>
                          {item?.price && (
                            <Typography className={classes.itemPrice}>
                              {Dinero({ amount: getPrice(item.price, item.quantity) }).toFormat('$0.00')}
                            </Typography>
                          )}
                        </Grid>
                        <Typography variant='body2' className={classes.subtitle}>Qty: <span style={{fontWeight: 700,}}>{item.quantity || 0}</span></Typography>
                        {item.notes ? (
                          <Box className={classes.customOrderDetailsContainer}>
                            <Box className={classes.customOrderDetailsValues}>
                              <Typography variant='body2' className={classes.notesTitle}>Notes</Typography>
                              <Typography variant='body1'>{item.notes}</Typography>
                            </Box>
                          </Box>
                        ) : (
                          <>
                            {transaction.artisanNotes && (
                              <Box className={classes.customOrderDetailsContainer}>
                                <Box className={classes.customOrderDetailsValues}>
                                  <Typography variant='body2' className={classes.notesTitle}>Notes</Typography>
                                  <Typography>{transaction.artisanNotes}</Typography>
                                </Box>
                              </Box>
                            )}
                          </>
                        )}
                      </div>
                    </Container>
                  </Container>
                  {item.subLineItems?.map(subItem => (
                    <Container key={subItem.id} className={classes.itemBox}  style={index === orderedItems.length - 1 ? {borderBottom: 'none'} : {}}>
                      <Container className={classes.itemContainer}>
                        <div className={classes.itemDetails}>
                          <Grid container justify="space-between" wrap="nowrap">
                            <Typography variant='body1'>{subItem.title}</Typography>
                            {subItem?.price && (
                              <Typography className={classes.itemPrice}>
                                {Dinero({ amount: getPrice(subItem.price, subItem.quantity) }).toFormat('$0.00')}
                              </Typography>
                            )}
                          </Grid>
                          <Typography variant='body2' className={classes.subtitle}>Qty: <span style={{fontWeight: 700,}}>{subItem.quantity || 0}</span></Typography>
                          {subItem.notes && (
                            <Box className={classes.customOrderDetailsContainer}>
                              <Box className={classes.customOrderDetailsValues}>
                                <Typography variant='body2' className={classes.notesTitle}>Notes</Typography>
                                <Typography variant='body1'>{subItem.notes}</Typography>
                              </Box>
                            </Box>
                          )}
                        </div>
                      </Container>
                    </Container>
                  ))}
                </>
              )}
            </>
          ))}

        {transaction?.notes && (
          <Box className={classes.notesSection}>
            <CustomerNotes notes={transaction.notes} />
          </Box>
        )}

        {/* Fees and Totals Section */}
        {!removePaymentDetails &&
          <Box className={classes.amountsSection} style={transaction?.notes ? {paddingTop: 0} : {borderTop: `1px solid ${theme.branding.gray[400]}`}}>
            <div className={classes.priceSection}>
              <Typography variant='body2'>Subtotal</Typography>
              {transaction?.totals?.subtotal && (
                <Typography variant='body2'>
                  {Dinero({ amount: transaction.totals.subtotal }).toFormat('$0.00')}
                </Typography>
              )}
            </div>
            {couponTotal != 0 && (
              <div className={classes.priceSection}>
                <Typography variant='body2'>Coupon ({couponCode})</Typography>
                <Typography variant='body2'>
                  -{Dinero({ amount: couponTotal || 0 }).toFormat('$0.00')}{' '}
                  {couponDiscount && couponDiscount.type === 'percent' ? `(${couponDiscount.value}%)` : ''}
                </Typography>
              </div>
            )}
            <FeatureFlag name="feature_tipping">
              {tip > 0 && (<>
                <div className={classes.priceSection}>
                  <Typography variant='body2'>Tip</Typography>
                  <Typography variant='body2'>{Dinero({ amount: tip }).toFormat('$0.00')}</Typography>
                </div>
                <div className={classes.hasTip}>
                  <Typography variant='body2'><span>&#128151;</span> You received a tip on this order! Keep up the great
                    work.</Typography>
                </div>
              </>)}
            </FeatureFlag>
            {fulfillmentFee != 0 && (
              <div className={classes.priceSection}>
                <Typography variant='body2'>{fulfillmentType}</Typography>
                <Typography variant='body2'>
                  {Dinero({ amount: fulfillmentFee || 0 }).toFormat('$0.00')}
                </Typography>
              </div>
            )}
            {!!customerCastironFeePercent && (
              <div className={classes.priceSection}>
                <Typography variant='body2'>
                  Castiron fees ({customerCastironFeePercent * 100}% paid by customer)
                </Typography>
                <Typography variant='body2'>
                  {Dinero({ amount: customerCastironFeeAmount }).toFormat('$0.00')}
                </Typography>
              </div>
            )}
            {customerPaidStripeFees && (
              <div className={classes.priceSection}>
                <Typography variant='body2'>Credit card processing fees (paid by customer)</Typography>
                <Typography variant='body2'>{Dinero({ amount: stripeFees }).toFormat('$0.00')}</Typography>
              </div>
            )}
            <div className={classes.priceSection}>
              <Typography variant='body2'>Sales tax</Typography>
              <Typography variant='body2'>{Dinero({ amount: salesTax || 0 }).toFormat('$0.00')}</Typography>
            </div>
            <div className={`${classes.priceSection} ${classes.totalSection}`}>
              <Typography variant='subtitle3'>Total</Typography>
              <Typography variant='subtitle3'>{Dinero({ amount: total || 0 }).toFormat('$0.00')}</Typography>
            </div>
          </Box>
        }
      </Card>
      {!!artisanCastironFeePercent && (
        <Card
          title={
            <div>
              Fees remaining on this order
              <Tooltip
                title="You don’t need to take any action to pay these fees. If they are still owed, they will be automatically deducted from your next payout" />
            </div>
          }
          removeChildPadding
        >
          <Box className={classes.amountsSection}>
            <div className={classes.priceSection}>
              <Typography variant='body4'>
                Castiron fees ({artisanCastironFeePercent * 100}% paid by you)
              </Typography>
              <Typography variant='body4'>
                {Dinero({
                  amount: artisanCastironFeeAmount,
                }).toFormat('$0.00')}
              </Typography>
            </div>
            {!customerPaidStripeFees && (
              <div className={classes.priceSection}>
                <Typography variant='body4'>Credit card processing fees (paid by you)</Typography>
                <Typography variant='body4'>{Dinero({ amount: stripeFees }).toFormat('$0.00')}</Typography>
              </div>
            )}
            <div className={`${classes.priceSection} ${classes.totalSection}`}>
              <Typography variant='subtitle3'>Total</Typography>
              <Typography variant='subtitle3'>
                {Dinero({ amount: artisanCastironFeeAmount + (!customerPaidStripeFees ? stripeFees : 0) }).toFormat(
                  '$0.00',
                )}
              </Typography>
            </div>
          </Box>
        </Card>
      )}
    </>
  );
};

export default DetailsBox;
